import { merge } from "lodash-es";
import { FC, useMemo, useState } from "react";

import { ScheduleBlockEntry } from "Bands/components";
import { IScheduleBlockEntry } from "Bands/types";

import { copy } from "shared/assets";
import { Filter } from "shared/assets/icons";
import { ActionDropdown, Message } from "shared/components";
import { LocalStorageService } from "shared/services";
import { ILocalStorageKey } from "shared/types";
import { formatDateTime, utcStringToDateTimeTimezone } from "shared/utils";

import { ScheduleBlockEntryDateTime } from "Bands/components/ScheduleBlockEntry/styles";
import { useBandContext } from "Bands/contexts";
import { DeleteScheduleBlockEntryModal, EditScheduleBlockEntryModal } from "..";
import { NotFlexSpacer } from "./styles";
import { parseJWT } from "Auth/utils";
import { getImpersonateCookie } from "shared/utils/getImpersonateCookie";

interface EventScheduleProps {
  blockId: string;
  entries: IScheduleBlockEntry[];
  showFilter?: boolean;
  showActions?: boolean;
  filterType?: ScheduleFilterType;
  showEditEntryModal?: IScheduleBlockEntry | null;
  setShowEditEntryModal?: (v: IScheduleBlockEntry | null) => void;
}

export enum ScheduleFilterType {
  full = "full",
  personal = "personal",
}

export const EventSchedule: FC<EventScheduleProps> = ({
  blockId,
  entries,
  showFilter,
  showActions,
  filterType,
  showEditEntryModal,
  setShowEditEntryModal,
}) => {
  const { currentBandUser, findBandUserByUserId } = useBandContext();
  const { scheduleBlock: scheduleBlockCopy } = copy;

  const [showDeleteEntryModal, setShowDeleteEntryModal] =
    useState<IScheduleBlockEntry | null>();

  const storedFilter = LocalStorageService.getItem(
    ILocalStorageKey.scheduleBlockFilter
  ) as { [key: string]: ScheduleFilterType };

  const [showSchedule, setShowSchedule] = useState<ScheduleFilterType>(
    showFilter
      ? filterType || storedFilter?.[blockId] || ScheduleFilterType.full
      : ScheduleFilterType.full
  );

  const handleChangeFilter = (type: ScheduleFilterType) => {
    setShowSchedule(type);
    LocalStorageService.setItem(
      ILocalStorageKey.scheduleBlockFilter,
      merge({}, storedFilter, { [blockId]: type })
    );
  };

  const currentScheduleUser = useMemo(() => {
    const impersonate = parseJWT(getImpersonateCookie());

    const impersonateUser =
      impersonate && findBandUserByUserId(impersonate.username);

    return impersonateUser ? impersonateUser : currentBandUser;
  }, [currentBandUser, findBandUserByUserId]);

  const myEntries = useMemo(
    () =>
      entries.filter(
        (entry) =>
          !entry.users?.length ||
          entry.users
            .map((user) => user.id)
            .includes(currentScheduleUser?.id || "")
      ),
    [currentScheduleUser?.id, entries]
  );

  const filteredEntries = useMemo(
    () => (showSchedule === "full" ? entries : myEntries),
    [entries, myEntries, showSchedule]
  );

  const entriesPerDay = useMemo(() => {
    let perDay: { [key: string]: IScheduleBlockEntry[] } = {};

    // group per day
    filteredEntries.forEach((entry) => {
      const { date: startDate } = utcStringToDateTimeTimezone(entry.start);

      perDay = merge({}, perDay, {
        [startDate]: [...(perDay?.[startDate] || []), entry],
      });
    });

    // sort entries per day
    Object.keys(perDay).map(
      (day) =>
        (perDay[day] = perDay[day].sort((a, b) => {
          if (a.start === b.start) {
            return new Date(a.end).getTime() > new Date(b.end).getTime()
              ? 1
              : -1;
          }

          return new Date(a.start).getTime() > new Date(b.start).getTime()
            ? 1
            : -1;
        }))
    );

    return perDay;
  }, [filteredEntries]);

  return (
    <>
      {showEditEntryModal && (
        <EditScheduleBlockEntryModal
          blockId={blockId}
          entry={showEditEntryModal}
          handleClose={() =>
            setShowEditEntryModal && setShowEditEntryModal!(null)
          }
        />
      )}
      {showDeleteEntryModal && (
        <DeleteScheduleBlockEntryModal
          blockId={blockId}
          entry={showDeleteEntryModal}
          handleClose={() => setShowDeleteEntryModal(null)}
        />
      )}
      <NotFlexSpacer size="xl">
        {showFilter && !!entries.length && (
          <ActionDropdown
            label={
              showSchedule === ScheduleFilterType.full
                ? "Filter: Full schedule"
                : "Filter: My schedule"
            }
            icon={Filter}
            options={[
              {
                label: "Full schedule",
                handleClick: () => handleChangeFilter(ScheduleFilterType.full),
                isSelected: showSchedule === ScheduleFilterType.full,
              },
              {
                label: "My schedule",
                handleClick: () =>
                  handleChangeFilter(ScheduleFilterType.personal),
                isSelected: showSchedule === ScheduleFilterType.personal,
              },
            ]}
            placement="bottom-start"
            dataTestId="schedule_block_filter"
          />
        )}
        {!filteredEntries.length && (
          <Message message={scheduleBlockCopy.view.empty} />
        )}
        {Object.keys(entriesPerDay)
          .sort((a, b) =>
            new Date(a).getTime() > new Date(b).getTime() ? 1 : -1
          )
          .map((day) => (
            <NotFlexSpacer size="s" key={day}>
              <ScheduleBlockEntryDateTime>
                {formatDateTime(day, {
                  dateOnly: true,
                  longForm: true,
                })}
              </ScheduleBlockEntryDateTime>
              <div data-testid="event_schedule_table">
                {entriesPerDay[day].map((entry, index) => (
                  <ScheduleBlockEntry
                    key={entry.id}
                    index={index}
                    entry={entry}
                    showActions={showActions}
                    setShowEditEntryModal={setShowEditEntryModal}
                    setShowDeleteEntryModal={setShowDeleteEntryModal}
                  />
                ))}
              </div>
            </NotFlexSpacer>
          ))}
      </NotFlexSpacer>
    </>
  );
};
